import React, { useRef } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useIsOverflow } from './useIsOverflow';

interface SiteChipProps {
  siteNames: string[];
  totalSiteCount: number;
  siteRestricted: boolean;
}

const popoverWithinViewport = {
  modifiers: [
    {
      name: 'flip',
      options: { fallbackPlacements: ['left', 'bottom-start'], boundary: document.documentElement, padding: 8 },
    },
    { name: 'preventOverflow', options: { boundary: document.documentElement, padding: 8 } },
  ],
};

const labelFor = (props: SiteChipProps) => {
  if (!props.siteRestricted) return 'All sites';
  if (props.siteNames.length === 0) return 'Unassigned';
  if (props.siteNames.length === 1) return props.siteNames[0];

  return `${props.siteNames.length} of ${props.totalSiteCount}`;
};

export const SiteChip = (props: SiteChipProps) => {
  const label = labelFor(props);
  const listed = props.siteRestricted && props.siteNames.length > 1;
  const empty = props.siteRestricted && props.siteNames.length === 0;
  const nameRef = useRef<HTMLParagraphElement>(null);
  const clipped = useIsOverflow(nameRef);
  const expandable = listed || clipped;

  const overlay = expandable
    ? <Popover className="border" style={{ maxWidth: 'none' }}>
      <Popover.Body className="small py-2 px-3 text-nowrap">
        {listed
          ? <ul className="mb-0 ps-3">
            {props.siteNames.map(name => <li key={name}>{name}</li>)}
          </ul>
          : label}
      </Popover.Body>
    </Popover>
    : <></>;

  return <OverlayTrigger
    placement="right"
    flip
    container={document.body}
    popperConfig={popoverWithinViewport}
    overlay={overlay}>
    <div
      className="d-inline-flex align-items-center gap-1 mw-100 small"
      tabIndex={expandable ? 0 : undefined}
      aria-label={listed ? `${label}: ${props.siteNames.join(', ')}` : undefined}>
      <i className={`bi-geo-alt-fill flex-shrink-0 ${empty ? 'text-warning' : 'text-muted'}`} aria-hidden="true" />
      {listed
        ? <span className="text-truncate" style={{ borderBottom: '1px dotted currentColor', cursor: 'default' }}>{label}</span>
        : <p className="line-clamp line-clamp-2 mb-0" ref={nameRef}>{label}</p>}
    </div>
  </OverlayTrigger>;
};
